import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Filter, RefreshCw, XCircle } from 'lucide-react';

const EC2_URL = 'https://api.algrok.com';

function nowIST() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}

function useWindowWidth() {
  var get = function() { return window.innerWidth; };
  var [w, setW] = useState(get());
  useEffect(function() { var h = function() { setW(get()); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, []);
  return w;
}

export default function Orders() {
  var winW = useWindowWidth();
  var isCompact = winW < 1080;
  var isTight = winW < 900;
  const [orders,    setOrders]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [filter,    setFilter]    = useState('ALL');
  const [error,     setError]     = useState('');
  const [cancelling, setCancelling] = useState({});

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(EC2_URL + '/orders');
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setOrders(Array.isArray(d) ? d : (d.data || []));
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto refresh every 10s during market hours
  useEffect(() => {
    const id = setInterval(() => {
      const n = nowIST();
      const h = n.getHours(), m = n.getMinutes();
      if ((h > 9 || (h===9 && m>=15)) && h < 16) fetchOrders();
    }, 10000);
    return () => clearInterval(id);
  }, [fetchOrders]);

  const cancelOrder = async (order) => {
    setCancelling(c => ({ ...c, [order.order_id]: true }));
    try {
      const r = await fetch(EC2_URL + '/cancel-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: order.order_id, variety: order.variety || 'regular' }),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      await fetchOrders();
    } catch(e) {
      alert('Cancel failed: ' + e.message);
    } finally {
      setCancelling(c => ({ ...c, [order.order_id]: false }));
    }
  };

  const filters = ['ALL','COMPLETE','OPEN','AMO REQ RECEIVED','REJECTED','CANCELLED'];
  const filtered = filter === 'ALL' ? orders : orders.filter(o => o.status === filter);

  const total     = orders.length;
  const completed = orders.filter(o => o.status === 'COMPLETE').length;
  const open      = orders.filter(o => ['OPEN','AMO REQ RECEIVED','TRIGGER PENDING'].includes(o.status)).length;
  const rejected  = orders.filter(o => ['REJECTED','CANCELLED'].includes(o.status)).length;

  const statusColor = (s) => {
    if (s === 'COMPLETE')                    return { color:'#00d4aa', bg:'rgba(0,212,170,0.1)' };
    if (['OPEN','AMO REQ RECEIVED','TRIGGER PENDING'].includes(s)) return { color:'#0ea5e9', bg:'rgba(14,165,233,0.1)' };
    if (['REJECTED','CANCELLED'].includes(s))return { color:'#ff4466', bg:'rgba(255,68,102,0.1)' };
    return { color:'#8899aa', bg:'rgba(136,153,170,0.1)' };
  };

  const canCancel = (s) => ['OPEN','AMO REQ RECEIVED','TRIGGER PENDING'].includes(s);

  return (
    <div style={{ padding: 20, fontFamily: 'monospace', color: '#e2e8f0', height: '100%', overflowY: 'auto' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 20, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:isCompact?15:18, fontWeight:700 }}>Orders</div>
          <div style={{ fontSize:11, color:'#8899aa' }}>Live from Zerodha · auto-refresh every 10s</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <Filter size={13} style={{ color:'#8899aa' }} />
          {filters.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding:'4px 10px', borderRadius:4, fontSize:10, cursor:'pointer', fontWeight:700,
                background: filter===f ? 'rgba(14,165,233,0.2)' : 'transparent',
                color: filter===f ? '#0ea5e9' : '#8899aa',
                border:`1px solid ${filter===f ? 'rgba(14,165,233,0.4)' : 'rgba(26,45,74,0.4)'}` }}>
              {f}
            </button>
          ))}
          <button onClick={fetchOrders} disabled={loading}
            style={{ padding:'4px 10px', borderRadius:4, fontSize:11, cursor:'pointer',
              background:'rgba(14,165,233,0.1)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.3)',
              display:'flex', alignItems:'center', gap:4 }}>
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:isCompact?'repeat(2,1fr)':'repeat(4,1fr)', gap:isCompact?8:12, marginBottom:20 }}>
        {[
          { label:'Total Orders', value:total,     color:'#e2e8f0' },
          { label:'Completed',    value:completed, color:'#00d4aa' },
          { label:'Open / AMO',   value:open,      color:'#0ea5e9' },
          { label:'Rejected',     value:rejected,  color:'#ff4466' },
        ].map(s => (
          <div key={s.label} style={{ background:'#0d1526', border:'1px solid rgba(26,45,74,0.7)', borderRadius:8, padding:'14px 16px' }}>
            <div style={{ fontSize:10, color:'#8899aa', marginBottom:6, fontWeight:700, textTransform:'uppercase' }}>{s.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding:'10px 16px', background:'rgba(255,68,102,0.1)', border:'1px solid rgba(255,68,102,0.3)', borderRadius:6, color:'#ff4466', fontSize:12, marginBottom:16 }}>
          ❌ {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background:'#0d1526', border:'1px solid rgba(26,45,74,0.7)', borderRadius:8, overflow:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
          <thead>
            <tr style={{ background:'#080e1d' }}>
              {['Time','Source','Order ID','Instrument','Action','Qty','Price','Type','Product','Variety','Status','Cancel'].map(h => (
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:10, color:'#6b7a8d',
                  fontWeight:700, textTransform:'uppercase', borderBottom:'1px solid rgba(26,45,74,0.5)', whiteSpace:'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={12} style={{ padding:40, textAlign:'center', color:'#4a5568' }}>
                {loading ? 'Loading orders...' : 'No orders found'}
              </td></tr>
            ) : filtered.map((o, i) => {
              const sc = statusColor(o.status);
              const tag = (o.tag || '').toLowerCase();
              const SOURCE_MAP = {
                'autodn':        { label: 'Auto DN',   color: '#0ea5e9' },
                'amb':           { label: 'AMB',        color: '#f97316' },
                'straddle':      { label: 'Straddle',   color: '#ec4899' },
                'positional_tv': { label: 'JP Signal',  color: '#a78bfa' },
                'quantiply':     { label: 'Quantiply',  color: '#fbbf24' },
              };
              const src = SOURCE_MAP[tag] || (tag.startsWith('qty_') ? { label: 'Qty ' + tag.slice(4).toUpperCase(), color: '#fbbf24' } : null) || (tag ? { label: tag, color: '#556677' } : null);
              return (
                <tr key={o.order_id || i} style={{ borderBottom:'1px solid rgba(26,45,74,0.2)',
                  background: i%2===0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                  <td style={{ padding:'8px 12px', color:'#8899aa', whiteSpace:'nowrap' }}>
                    {o.order_timestamp ? new Date(o.order_timestamp).toLocaleTimeString('en-IN') : '--'}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    {src ? <span style={{ padding:'2px 8px', borderRadius:3, fontSize:9, fontWeight:800,
                      color: src.color, background: src.color + '18', border: '1px solid ' + src.color + '30' }}>
                      {src.label}
                    </span> : <span style={{ color:'#3d4f63', fontSize:9 }}>Manual</span>}
                  </td>
                  <td style={{ padding:'8px 12px', color:'#4a5568', fontSize:10 }}>{o.order_id}</td>
                  <td style={{ padding:'8px 12px', color:'#e2e8f0', fontWeight:700 }}>{o.tradingsymbol}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ padding:'2px 8px', borderRadius:3, fontSize:10, fontWeight:700,
                      color: o.transaction_type==='BUY' ? '#00d4aa' : '#ff4466',
                      background: o.transaction_type==='BUY' ? 'rgba(0,212,170,0.1)' : 'rgba(255,68,102,0.1)' }}>
                      {o.transaction_type==='BUY' ? '▲ BUY' : '▼ SELL'}
                    </span>
                  </td>
                  <td style={{ padding:'8px 12px' }}>{o.quantity}</td>
                  <td style={{ padding:'8px 12px', color:'#e2e8f0' }}>
                    {o.average_price > 0 ? o.average_price.toFixed(2) : o.price?.toFixed(2) || '--'}
                  </td>
                  <td style={{ padding:'8px 12px', color:'#8899aa' }}>{o.order_type}</td>
                  <td style={{ padding:'8px 12px', color:'#8899aa' }}>{o.product}</td>
                  <td style={{ padding:'8px 12px', color:'#8899aa' }}>{o.variety}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ padding:'2px 8px', borderRadius:3, fontSize:10, fontWeight:700,
                      color:sc.color, background:sc.bg }}>
                      {o.status}
                    </span>
                    {o.status_message && <div style={{ fontSize:9, color:'#4a5568', marginTop:2 }}>{o.status_message}</div>}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    {canCancel(o.status) && (
                      <button onClick={() => cancelOrder(o)} disabled={cancelling[o.order_id]}
                        style={{ padding:'2px 8px', borderRadius:3, fontSize:10, cursor:'pointer',
                          background:'rgba(255,68,102,0.1)', color:'#ff4466',
                          border:'1px solid rgba(255,68,102,0.3)', display:'flex', alignItems:'center', gap:3 }}>
                        <XCircle size={10} />
                        {cancelling[o.order_id] ? '...' : 'Cancel'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
